import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const C = {
  navy:    '#0f172a',
  ink:     '#1e293b',
  muted:   '#64748b',
  border:  '#e2e8f0',
  indigo:  '#4f46e5',
  indigoXL:'#eef2ff',
  snow:    '#f8fafc',
  white:   '#ffffff',
  red:     '#dc2626',
  redBg:   '#fef2f2',
  redBorder:'#fca5a5',
};

const P = ({ children }) => (
  <p style={{ fontSize: '0.92rem', color: C.muted, lineHeight: 1.85, margin: '0 0 14px' }}>{children}</p>
);

const Block = ({ icon, title, body, variant = 'default' }) => {
  const bg    = variant === 'red'    ? C.redBg    : variant === 'amber' ? '#fffbeb' : C.snow;
  const bdr   = variant === 'red'    ? C.redBorder : variant === 'amber' ? '#fde68a' : C.border;
  const title_c = variant === 'red'  ? '#7f1d1d'  : variant === 'amber' ? '#78350f' : C.navy;
  const body_c  = variant === 'red'  ? '#991b1b'  : variant === 'amber' ? '#92400e' : C.muted;

  return (
    <div style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 14, padding: '20px 22px', marginBottom: 16, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <span style={{ fontSize: '1.5rem', flexShrink: 0, lineHeight: 1 }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 800, fontSize: '0.92rem', color: title_c, marginBottom: 6, letterSpacing: '-0.01em' }}>{title}</div>
        <div style={{ fontSize: '0.85rem', color: body_c, lineHeight: 1.75 }}>{body}</div>
      </div>
    </div>
  );
};

const LegalFooter = () => (
  <footer style={{ background: C.snow, padding: '24px clamp(20px,5vw,60px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, borderTop: `1px solid ${C.border}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <img src="/logo192.png" alt="AssessMonk" style={{ width: 22, height: 22, borderRadius: 6, objectFit: 'contain' }} />
      <span style={{ fontWeight: 800, fontSize: '0.82rem', color: C.navy }}>AssessMonk</span>
    </div>
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
      <Link to="/privacy"     style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</Link>
      <Link to="/terms"       style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Terms</Link>
      <Link to="/disclaimer"  style={{ fontSize: '0.72rem', color: C.indigo, textDecoration: 'none', fontWeight: 700 }}>Disclaimer</Link>
      <Link to="/refund"      style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Refund Policy</Link>
      <Link to="/contact"     style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Contact</Link>
    </div>
    <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>© 2026 AssessMonk. All rights reserved.</div>
  </footer>
);

export default function DisclaimerPage() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: C.white, color: C.navy, minHeight: '100vh' }}>

      <nav style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.border}`, padding: '0 clamp(20px,5vw,64px)', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 100 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: C.navy }}>AssessMonk</span>
        </Link>
        <button onClick={() => navigate('/app')} style={{ padding: '7px 18px', borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', background: C.indigo, color: '#fff', border: 'none', cursor: 'pointer' }}>
          Try AI Assessment
        </button>
      </nav>

      {/* Hero */}
      <div style={{ background: `linear-gradient(to bottom, ${C.redBg}, ${C.white})`, padding: 'clamp(48px,8vw,80px) clamp(20px,5vw,64px) 0' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.redBg, border: `1px solid ${C.redBorder}`, borderRadius: 99, padding: '5px 14px', fontSize: '0.7rem', fontWeight: 700, color: C.red, marginBottom: 20, letterSpacing: '0.03em' }}>
            ⚠ Important Disclaimer
          </div>
          <h1 style={{ fontSize: 'clamp(1.9rem,4vw,2.8rem)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.03em', margin: '0 0 16px' }}>
            Clinical Disclaimer
          </h1>
          <p style={{ fontSize: '1rem', color: C.muted, lineHeight: 1.75, maxWidth: 600, margin: '0 0 24px' }}>
            AssessMonk is a clinical reasoning education platform. Please read this disclaimer carefully before using any AI-generated content from this platform.
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: C.muted, fontWeight: 600, background: C.snow, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 14px', marginBottom: 48 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
            Last updated: 25 April 2026
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '48px clamp(20px,5vw,64px) 80px' }}>

        {/* Primary warning banner */}
        <div style={{ background: C.redBg, border: `2px solid ${C.redBorder}`, borderRadius: 16, padding: '24px 28px', marginBottom: 40, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>⚠</div>
          <div style={{ fontSize: 'clamp(1rem,2vw,1.2rem)', fontWeight: 900, color: C.red, marginBottom: 10, letterSpacing: '-0.02em' }}>
            AssessMonk does not provide medical advice.
          </div>
          <div style={{ fontSize: '0.9rem', color: '#7f1d1d', lineHeight: 1.75, maxWidth: 560, margin: '0 auto' }}>
            AI-generated outputs on this platform are for educational and professional development purposes only. They are not a substitute for qualified clinical assessment, professional judgment, or licensed physiotherapy practice.
          </div>
        </div>

        <Block
          icon="🎓"
          title="Educational Platform — Not a Clinical Tool"
          body="AssessMonk is designed to support the education and professional development of physiotherapy students and clinicians. The platform teaches structured clinical reasoning. It is not a medical device, a clinical records system, or a diagnostic service."
          variant="default"
        />

        <Block
          icon="🤖"
          title="AI Outputs Are Educational References Only"
          body="Differential diagnoses, rehabilitation plans, SMART goals, prognosis estimates, and clinical recommendations generated by AssessMonk's AI are educational outputs based on the structured data you enter. They are not confirmed diagnoses. No AI output from this platform should be treated as clinical fact or applied directly to a real patient without professional review."
          variant="red"
        />

        <Block
          icon="🩺"
          title="Not a Substitute for Licensed Clinical Assessment"
          body="The structured findings you enter into AssessMonk represent a simulated or practice clinical scenario. The platform does not have access to real patient imaging, laboratory investigations, or direct physical examination. No output from this platform replaces a face-to-face assessment conducted by a qualified, registered physiotherapist."
          variant="amber"
        />

        <Block
          icon="❌"
          title="No Confirmed Diagnosis From This Platform"
          body="AssessMonk does not confirm diagnoses. AI-generated primary impressions and differential lists are reasoning aids only. They represent plausible clinical possibilities based on user-entered data, not a definitive clinical conclusion. A differential output from this platform must not be recorded as a clinical diagnosis in any patient health record."
          variant="red"
        />

        <Block
          icon="🚫"
          title="Do Not Base Treatment Solely on AI Recommendations"
          body="Treatment plans generated by AssessMonk are educational frameworks based on evidence-based physiotherapy literature. They are starting points for clinical thinking, not prescriptions. No treatment should be initiated for a real patient solely on the basis of an AI-generated plan from this platform. All treatment decisions must be made by a qualified clinician using direct clinical assessment."
          variant="amber"
        />

        <Block
          icon="👤"
          title="User Remains Fully Responsible for Patient Care"
          body="If you are a registered clinician using AssessMonk to support your reasoning, you remain the responsible clinician for all decisions made in relation to your patients. AssessMonk, its operators, and its technology partners accept no responsibility for clinical outcomes arising from reliance on platform outputs."
          variant="default"
        />

        <div style={{ background: C.indigoXL, border: '1px solid #c7d2fe', borderRadius: 14, padding: '20px 24px', marginBottom: 32, marginTop: 32 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 800, color: C.indigo, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 8 }}>Summary</div>
          <P>By using AssessMonk, you acknowledge and accept that:</P>
          <ul style={{ margin: '0', paddingLeft: 0, listStyle: 'none' }}>
            {[
              'AssessMonk is an educational platform, not a medical device or clinical records system.',
              'AI-generated outputs are educational references, not clinical diagnoses.',
              'No treatment decision should be based solely on AI outputs from this platform.',
              'You remain fully responsible for all clinical decisions relating to your patients.',
              'Outputs must be reviewed and validated by a supervising qualified clinician before application to real patient care.',
            ].map((item, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '4px 0', fontSize: '0.88rem', color: C.ink, lineHeight: 1.65 }}>
                <span style={{ color: C.indigo, fontWeight: 800, flexShrink: 0 }}>✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 32 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 800, color: C.navy, marginBottom: 8 }}>Questions or concerns?</div>
          <P>Contact us at <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a>. For full legal terms, see our <Link to="/terms" style={{ color: C.indigo, fontWeight: 600 }}>Terms & Conditions</Link> and <Link to="/privacy" style={{ color: C.indigo, fontWeight: 600 }}>Privacy Policy</Link>.</P>
        </div>

      </div>

      <LegalFooter />
    </div>
  );
}
